import { ComponentProps, useCallback, useMemo } from 'react';

import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  Scatter,
  TimeXAxis,
  YAxis,
} from '@cast/design-system';

import { OomKillBucketQueryRange } from './_hooks/useOomKillBucketWorkloadsQuery';
import { useOomKillWorkloadsDrawer } from './_hooks/useOomKillWorkloadsDrawer';
import { OomKillWorkloadHrefArgs } from './_hooks/useOomKillWorkloadsDrawer/OomKillWorkloadsDrawer';
import { OomKillTooltip } from './OomKillTooltip';
import { OOMKillChartItem } from './types';
import { oomKillsColorMap } from './utils';

type OomKillChartPoint = OOMKillChartItem & {
  totalCount?: number | null;
};

type ChartContainerPropBag = ComponentProps<typeof ChartContainer>;

type OomKillsScatterChartProps = {
  data: OomKillChartPoint[];
  timeZone: string;
  isLoading?: boolean;
  hasNoData?: boolean;
  testId?: string;
  rangeSelection?: OomKillBucketQueryRange;
  getWorkloadHref?: (args: OomKillWorkloadHrefArgs) => string;
  /** When false, shows breakdown tooltip but does not open the drawer. */
  openDrawerOnClick?: boolean;
  zoomProps?: ChartContainerPropBag['zoomProps'];
  analyzeWithOpsPilot?: ChartContainerPropBag['analyzeWithOpsPilot'];
  chartActionsSx?: ChartContainerPropBag['chartActionsSx'];
  sx?: ChartContainerPropBag['sx'];
};

export const OomKillsScatterChart = ({
  data,
  timeZone,
  isLoading,
  hasNoData,
  testId = 'oom-kills-chart',
  rangeSelection,
  getWorkloadHref,
  openDrawerOnClick = true,
  zoomProps,
  analyzeWithOpsPilot,
  chartActionsSx,
  sx,
}: OomKillsScatterChartProps) => {
  const { openOomKillWorkloadsDrawer } = useOomKillWorkloadsDrawer();

  const hasLifecycleSplit = useMemo(
    () =>
      data.some(
        (point) => point.managedCount != null || point.unmanagedCount != null
      ),
    [data]
  );
  const hasTotalOnly = useMemo(
    () => data.some((point) => point.totalCount != null),
    [data]
  );

  const handleBubbleClick = useCallback(
    (point: OomKillChartPoint, managed: boolean) => {
      if (!openDrawerOnClick || !hasLifecycleSplit) {
        return;
      }
      const count = managed ? point.managedCount : point.unmanagedCount;
      if (!point.timestamp || count == null) {
        return;
      }
      openOomKillWorkloadsDrawer({
        bucketTimestamp: point.timestamp,
        managed,
        timeZone,
        rangeSelection,
        getWorkloadHref,
      });
    },
    [
      openDrawerOnClick,
      hasLifecycleSplit,
      openOomKillWorkloadsDrawer,
      timeZone,
      rangeSelection,
      getWorkloadHref,
    ]
  );

  return (
    <ChartContainer
      data={data}
      isLoading={isLoading}
      hasNoData={hasNoData}
      noDataOverlayProps={{ title: 'No events were observed' }}
      testId={testId}
      zoomProps={zoomProps}
      analyzeWithOpsPilot={analyzeWithOpsPilot}
      chartActionsSx={chartActionsSx}
      sx={sx}
    >
      <ChartGrid hideVertical />
      {hasTotalOnly && !hasLifecycleSplit ? (
        <Scatter<OomKillChartPoint>
          dataKey="totalCount"
          shapeProps={{
            shapeColor: oomKillsColorMap.totalCount,
            shapeSize: 'small',
          }}
        />
      ) : (
        <>
          <Scatter<OomKillChartPoint>
            dataKey="unmanagedCount"
            shapeProps={{
              shapeColor: oomKillsColorMap.unmanagedCount,
              shapeSize: 'small',
            }}
            hideActiveShape
            onShapeClick={
              openDrawerOnClick
                ? (point) => handleBubbleClick(point, false)
                : undefined
            }
            testId="oom-unmanaged-scatter"
          />
          <Scatter<OomKillChartPoint>
            dataKey="managedCount"
            shapeProps={{
              shapeColor: oomKillsColorMap.managedCount,
              shapeSize: 'small',
            }}
            hideActiveShape
            onShapeClick={
              openDrawerOnClick
                ? (point) => handleBubbleClick(point, true)
                : undefined
            }
            testId="oom-managed-scatter"
          />
        </>
      )}
      <TimeXAxis<OomKillChartPoint>
        unixDataKey="chartTimestampUnix"
        timezone={timeZone}
      />
      <YAxis<OomKillChartPoint> allowDecimals={false} />
      <ChartTooltip<OomKillChartPoint>
        TooltipComponent={(props) => (
          <OomKillTooltip
            {...props}
            timeZone={timeZone}
            interactive={hasLifecycleSplit}
            rangeSelection={rangeSelection}
          />
        )}
      />
    </ChartContainer>
  );
};
